// game/client/src/hud/onboarding.ts
//
// Low-touch new-player onboarding (issue #1, 2026-06-18). Two of the three
// agreed layers live here (the third — a static how-to card — lives on the
// landing screen, menu/landing.ts):
//
//   • OBJECTIVE TICKER — a persistent pill showing the current suggested goal,
//     which advances with your progress and retires once you're established
//     (day 3+). Goals can declare TRACKED SUB-GOALS (named resource thresholds
//     read live from inventory) that render as progress chips ("Wood 3/5") and
//     tick to "done" as you collect — the general "spell out + track" principle
//     for the goals system, reusable by any future objective.
//   • JUST-IN-TIME TIPS — one-line hints that fire ONCE on the first occurrence
//     of a teachable moment (first move, standing by a resource, first skill
//     point, edible food in the bag, first creature drop). Remembered in
//     localStorage so a returning player is never re-taught — teach by doing.
//   • STICKY FREEZE WARNING — a small persistent pill that stays visible WHILE
//     the player is freezing and clears once warmth recovers (vs the one-shot
//     "build a campfire" teach, which fires only on the first cold snap). Driven
//     each frame off the warmth read with hysteresis so it never flickers.
//
// Both live in a LEFT-MIDDLE FEED (2026-06-18, Aviv): the goal pill on top, tips
// stacking below it. The old top-centre placement collided with the day/night
// clock; the left band is free now that the expertise arc moved above the plate.
//
// Pure presentation + a few state reads; no wire traffic. Dev hook
// window.__resetOnboarding() clears the "seen" flags for QA.

import { ITEMS, BUILDING_DEFS, getMaterial } from "@tribez/shared";
import type { Cube, HelloState, BuildingCost } from "@tribez/shared";

export interface OnboardCtx {
  cube: Cube;            // local player's cube (alive or dead)
  state: HelloState;     // room state (buildings, resources, dayCount…)
  mySessionId: string;
}

export interface Onboarding {
  update(ctx: OnboardCtx): void;
  dispose(): void;
}

const SEEN_PREFIX = "tribez:onboard:";
const TIP_MS = 7000;              // visible duration per tip
const TIP_GAP_MS = 1400;         // min spacing between tips so a fresh spawn (move +
                                 // spend-point + …) releases ONE at a time, not a flood
const MAX_TIPS = 4;               // cap concurrent tips in the feed
const HARVEST_RANGE_SQ = 8 * 8;   // "standing by a resource" radius²
const WARMTH_TIP_AT = 85;         // warmth below this → first cold-snap teach (one-shot)
const FREEZE_WARN_ON = 30;        // warmth ≤ this → show the sticky "find a fire" warning
const FREEZE_WARN_OFF = 55;       // warmth ≥ this → clear it (hysteresis avoids flicker)

function seen(id: string): boolean {
  try { return localStorage.getItem(SEEN_PREFIX + id) === "1"; } catch { return false; }
}
function markSeen(id: string): void {
  try { localStorage.setItem(SEEN_PREFIX + id, "1"); } catch { /* private mode */ }
}

const STYLE_ID = "onboarding-style";
const CSS = `
.onb-feed {
  position: fixed; left: 16px; top: 40%; z-index: 31;
  display: flex; flex-direction: column; gap: 8px; width: 268px;
  pointer-events: none;
}
.onb-ticker {
  display: none; align-self: flex-start; max-width: 100%;
  padding: 6px 14px; border-radius: 999px;
  background: linear-gradient(180deg, rgba(20,18,14,0.90), rgba(10,8,6,0.90));
  border: 1px solid rgba(200, 165, 90, 0.38);
  box-shadow: 0 2px 8px rgba(0,0,0,0.5);
  font: 700 12.5px/1.35 system-ui, sans-serif; color: #e8dcc0;
  text-shadow: 0 1px 2px rgba(0,0,0,0.6);
}
.onb-ticker.show { display: block; }
.onb-ticker__step { color: #f7c360; font-weight: 800; margin-right: 6px;
  text-transform: uppercase; letter-spacing: 0.05em; font-size: 10.5px; }

/* Tracked sub-goal chips under a goal (e.g. "Wood 3/5 · Stone 2/4"). */
.onb-ticker__goals { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 7px; }
.onb-goal {
  padding: 2px 8px; border-radius: 999px;
  background: rgba(60, 50, 30, 0.7); border: 1px solid rgba(200, 165, 90, 0.30);
  font: 700 10.5px/1.3 system-ui, sans-serif; color: #d8c89c; letter-spacing: 0.02em;
}
.onb-goal--done { background: rgba(40, 70, 35, 0.82);
  border-color: rgba(120, 200, 110, 0.55); color: #b9e6a8; }

/* Sticky freeze warning — persistent while cold, cleared on warm-up. */
.onb-warn {
  display: none; align-self: flex-start; max-width: 100%;
  padding: 6px 13px; border-radius: 999px;
  background: linear-gradient(180deg, rgba(20,30,44,0.92), rgba(10,16,26,0.92));
  border: 1px solid rgba(120, 180, 230, 0.50);
  box-shadow: 0 2px 8px rgba(0,0,0,0.5);
  font: 700 12px/1.35 system-ui, sans-serif; color: #cfe6ff;
  text-shadow: 0 1px 2px rgba(0,0,0,0.6);
}
.onb-warn.show { display: block; }
.onb-warn b { color: #9fd0ff; }

.onb-tip {
  align-self: flex-start; max-width: 100%;
  padding: 9px 13px; border-radius: 8px;
  border: 1px solid rgba(247, 200, 110, 0.42); border-left: 3px solid #f7c360;
  background: linear-gradient(180deg, rgba(28,24,18,0.96), rgba(14,11,8,0.96));
  box-shadow: 0 4px 14px rgba(0,0,0,0.55);
  font: 500 13px/1.45 system-ui, sans-serif; color: #f2e6cc;
  opacity: 0; transform: translateX(-12px);
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.onb-tip.show { opacity: 1; transform: translateX(0); }
.onb-tip b { color: #f7c360; font-weight: 700; }
`;

// ── Objective + tracked sub-goals ──────────────────────────────────────────
//
// A reusable shape for "spell out + track" goals (Aviv's general principle for
// the goals/quests system). An Objective is a one-line goal plus optional
// SubGoals — named thresholds with live progress. The ticker renders each
// SubGoal as a chip ("Wood 3/5") that flips to "done" when have >= need. Any
// future goal can declare subgoals without touching the renderer.
interface SubGoal {
  label: string; // player-facing resource name, e.g. "Wood"
  have: number;  // current count (clamped to `need` for display)
  need: number;  // required count
}
interface Objective {
  step: string;            // the small badge label, e.g. "Goal"
  text: string;            // goal line (trusted static HTML)
  subgoals?: SubGoal[];    // tracked thresholds, rendered as progress chips
}

// Build-cost resource keys → player-facing labels.
const RES_LABEL: Record<string, string> = {
  lumber: "Wood", stone: "Stone", ironOre: "Iron", woolOrLeather: "Hide",
};
const COST_KEYS: Array<keyof BuildingCost> = ["lumber", "stone", "ironOre", "woolOrLeather"];

/** How much of a build-cost key the player holds. woolOrLeather is the wool+leather
 *  sum (mirrors the server canAfford + Z-menu affordability check). */
function haveMaterial(cube: Cube, key: string): number {
  return key === "woolOrLeather"
    ? getMaterial(cube, "wool") + getMaterial(cube, "leather")
    : getMaterial(cube, key);
}

/** Turn a BuildingCost into tracked sub-goals read live from the player's inventory. */
function costSubgoals(cube: Cube, cost: BuildingCost): SubGoal[] {
  const out: SubGoal[] = [];
  for (const key of COST_KEYS) {
    const need = cost[key] ?? 0;
    if (need > 0) {
      out.push({ label: RES_LABEL[key] ?? key, have: Math.min(haveMaterial(cube, key), need), need });
    }
  }
  return out;
}

/** Change-detection signature so the ticker only re-renders when text/progress moves. */
function objectiveSig(obj: Objective | null): string {
  if (!obj) return "";
  const goals = obj.subgoals?.map((g) => `${g.label}${g.have}/${g.need}`).join(",") ?? "";
  return `${obj.step}|${obj.text}|${goals}`;
}

function renderObjective(obj: Objective): string {
  let html = `<span class="onb-ticker__step">${obj.step}</span>${obj.text}`;
  if (obj.subgoals && obj.subgoals.length > 0) {
    const chips = obj.subgoals
      .map((g) => {
        const done = g.have >= g.need;
        return `<span class="onb-goal${done ? " onb-goal--done" : ""}">${g.label} ${g.have}/${g.need}</span>`;
      })
      .join("");
    html += `<div class="onb-ticker__goals">${chips}</div>`;
  }
  return html;
}

export function createOnboarding(): Onboarding {
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const feed = document.createElement("div");
  feed.className = "onb-feed";

  const ticker = document.createElement("div");
  ticker.className = "onb-ticker";
  feed.appendChild(ticker); // goal pill pinned at the top of the feed

  // Sticky freeze warning: lives just under the goal pill, above the tip stack.
  // Toggled each frame off the warmth read (not the one-shot tip system).
  const warn = document.createElement("div");
  warn.className = "onb-warn";
  warn.innerHTML = `❄ <b>Freezing!</b> Find a fire — stand by a Campfire to warm up.`;
  feed.appendChild(warn);

  document.body.appendChild(feed);

  // Tips are QUEUED, not shown on the spot: several teachable conditions can be
  // true on the very first frame (move + a banked skill point + …), and dumping
  // them at once floods the feed. The queue releases one tip per TIP_GAP_MS.
  const queuedIds = new Set<string>();
  const tipQueue: { id: string; text: string }[] = [];
  let nextTipAt = 0;

  /** Enqueue a tip the first time only (localStorage-remembered). */
  function fireTip(id: string, text: string): void {
    if (seen(id) || queuedIds.has(id)) return;
    queuedIds.add(id);
    tipQueue.push({ id, text });
  }

  /** Release at most one queued tip per call, spaced by TIP_GAP_MS. */
  function pumpTips(nowMs: number): void {
    if (tipQueue.length === 0 || nowMs < nextTipAt) return;
    const { id, text } = tipQueue.shift()!;
    queuedIds.delete(id);
    nextTipAt = nowMs + TIP_GAP_MS;
    if (seen(id)) return; // raced (e.g. __resetOnboarding cleared mid-queue)
    markSeen(id);

    const row = document.createElement("div");
    row.className = "onb-tip";
    row.innerHTML = text;
    feed.appendChild(row);
    void row.offsetWidth; // reflow so the transition runs from hidden
    row.classList.add("show");

    // Cap concurrent tips (drop the oldest tip, never the ticker).
    const tips = feed.querySelectorAll(".onb-tip");
    if (tips.length > MAX_TIPS) tips[0]!.remove();

    window.setTimeout(() => {
      row.classList.remove("show");
      window.setTimeout(() => row.remove(), 400);
    }, TIP_MS);
  }

  let frame = 0;
  let lastTicker = "";
  let freezing = false; // sticky freeze-warning state (hysteresis-driven)

  function update(ctx: OnboardCtx): void {
    const { cube, state } = ctx;
    frame++;
    const alive = cube.state === "alive";
    pumpTips(performance.now());

    // ── Just-in-time tips ────────────────────────────────────────────────────
    if (alive) {
      fireTip("move", "<b>Right-click</b> the ground to move your kobold.");

      if ((cube.expertise?.bankedPoints ?? 0) > 0) {
        fireTip("spend", "Skill point earned! <b>Click a skill</b> above your bar to spend it.");
      }

      if ((cube.survival?.warmth ?? 100) < WARMTH_TIP_AT) {
        fireTip("warmth", "Getting cold — press <b>Z</b> to build a <b>Campfire</b>, then stand near it to warm up.");
      }

      if (!seen("harvest") && !queuedIds.has("harvest") && frame % 12 === 0) {
        // Early-out on the first in-range node instead of scanning every resource
        // each sample (the map carries hundreds of nodes).
        for (const r of state.resources.values()) {
          if (r.id.startsWith("drop-")) continue; // creature drops handled below
          const dx = r.x - cube.x, dz = r.z - cube.z;
          if (dx * dx + dz * dz <= HARVEST_RANGE_SQ) {
            fireTip("harvest", "<b>Right-click</b> a tree or rock to harvest it for materials.");
            break;
          }
        }
      }

      // #9 — teach raw eating the first time the player holds edible food. Meat
      // AND berries carry cookingBuff.restoreHunger, so any such backpack item is
      // directly edible (no recipe needed). Backpack is small (12 slots), and we
      // only scan until the tip has been seen.
      if (!seen("eatraw") && !queuedIds.has("eatraw")) {
        for (const slot of cube.backpack?.values() ?? []) {
          const def = slot.itemId ? ITEMS[slot.itemId] : undefined;
          if (def && (def.cookingBuff?.restoreHunger ?? 0) > 0) {
            fireTip("eatraw", "Hungry? <b>Right-click meat or berries</b> in your bag to eat them raw.");
            break;
          }
        }
      }

      // #4 — first time a creature drop is visible nearby (FoW-gated: drops in the
      // resources map are pickups the player can already see). Creature kills spawn
      // ResourceNodes id'd "drop-<n>" (server/src/creatures/drops.ts).
      if (!seen("loot") && !queuedIds.has("loot") && frame % 12 === 0) {
        for (const r of state.resources.values()) {
          if (r.id.startsWith("drop-")) {
            fireTip("loot", "<b>Right-click the glowing drop</b> to pick it up.");
            break;
          }
        }
      }
    }

    // ── Sticky freeze warning (#7) ───────────────────────────────────────────
    const warmth = cube.survival?.warmth ?? 100;
    if (!alive) freezing = false;
    else if (!freezing && warmth <= FREEZE_WARN_ON) freezing = true;
    else if (freezing && warmth >= FREEZE_WARN_OFF) freezing = false;
    warn.classList.toggle("show", freezing);

    // ── Objective ticker (with tracked sub-goals) ────────────────────────────
    const obj = computeObjective(ctx);
    const sig = objectiveSig(obj);
    if (sig !== lastTicker) {
      lastTicker = sig;
      if (obj) {
        ticker.innerHTML = renderObjective(obj);
        ticker.classList.add("show");
      } else {
        ticker.classList.remove("show");
      }
    }
  }

  function computeObjective(ctx: OnboardCtx): Objective | null {
    const { cube, state } = ctx;
    const day = state.dayCount ?? 1;
    if (day >= 3) return null; // established — retire the ticker

    let ownsCampfire = false;
    let ownsForge = false;
    state.buildings?.forEach((b) => {
      if (b.state === "destroyed") return;
      if (b.teamId !== cube.teamId) return;
      if (b.buildingType === "Campfire") ownsCampfire = true;
      else if (b.buildingType === "Forge") ownsForge = true;
    });

    if (!ownsCampfire) {
      return {
        step: "Goal",
        text: `Gather materials, then press <b style="color:#f7c360">Z</b> to build a <b>Campfire</b>`,
        subgoals: costSubgoals(cube, BUILDING_DEFS.Campfire.cost),
      };
    }
    if (day <= 1) {
      return { step: "Goal", text: `Stay near your Campfire to survive the first night` };
    }
    if (!ownsForge) {
      // #5 — spell the goal out + track the Forge cost as sub-goals.
      return {
        step: "Goal",
        text: `Collect resources and build a <b>Forge</b> — press <b style="color:#f7c360">Z</b> for the build menu`,
        subgoals: costSubgoals(cube, BUILDING_DEFS.Forge.cost),
      };
    }
    return { step: "Goal", text: `Arm up — left-click your <b>Forge</b> to craft gear` };
  }

  // Dev/QA: clear the "seen" flags so tips fire again on reload.
  (window as unknown as { __resetOnboarding?: () => void }).__resetOnboarding = () => {
    try {
      for (const k of Object.keys(localStorage)) {
        if (k.startsWith(SEEN_PREFIX)) localStorage.removeItem(k);
      }
    } catch { /* ignore */ }
  };

  function dispose(): void {
    feed.remove();
  }

  return { update, dispose };
}
